import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiEyeOff, FiSend, FiSave } from "react-icons/fi";
import {
  fetchPastoralContent,
  publishPastoralContent,
  savePastoralContent,
  unpublishPastoralContent,
} from "../../store/devotionSlice";

const emptyContent = {
  summary: "",
  commentary: "",
  meditation_question: "",
  prayer_direction: "",
  status: "draft",
  revision: null,
};

const PastoralContentEditor = () => {
  const { dailyPassageId, language } = useParams();
  const dispatch = useDispatch();
  const draftPayload = useSelector((state) => state.devotions.pastorContentDraft);
  const [draft, setDraft] = useState(emptyContent);

  useEffect(() => {
    dispatch(fetchPastoralContent({ dailyPassageId, language }));
  }, [dailyPassageId, dispatch, language]);

  useEffect(() => {
    setDraft({ ...emptyContent, ...(draftPayload?.content || {}) });
  }, [draftPayload]);

  const update = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const save = () => dispatch(savePastoralContent({ dailyPassageId, language, content: draft }));
  const contentId = draftPayload?.content?.id;

  return (
    <main className="min-h-screen bg-cover px-4 py-6" style={{ backgroundImage: "url('/images/brick.jpg')" }}>
      <div className="mx-auto max-w-4xl bg-white/90 p-5 rounded-lg shadow-sm">
        <Link to="/admin/devotions" className="text-sm font-semibold text-emerald-900 hover:underline">Back to dashboard</Link>
        <div className="mt-3">
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-900">{language?.toUpperCase()} Pastoral Content</p>
          <h1 className="text-2xl font-bold text-gray-900">{draftPayload?.passage?.reference_ko || "Daily Devotion"}</h1>
          <p className="text-gray-700">{draftPayload?.passage?.reference_en}</p>
        </div>

        <div className="mt-6 grid gap-4">
          {[
            ["summary", "Passage Summary", 4],
            ["commentary", "Pastor's Reflection", 8],
            ["meditation_question", "Today's Question", 3],
            ["prayer_direction", "Prayer Direction", 3],
          ].map(([field, label, rows]) => (
            <label key={field} className="grid gap-2 font-semibold text-gray-800">
              <span>{label}</span>
              <textarea
                value={draft[field] || ""}
                onChange={(event) => update(field, event.target.value)}
                rows={rows}
                className="rounded-md border border-gray-300 p-3 text-base font-normal leading-6"
              />
            </label>
          ))}
          <label className="grid gap-2 font-semibold text-gray-800">
            <span>Status</span>
            <select value={draft.status || "draft"} onChange={(event) => update("status", event.target.value)} className="min-h-[44px] rounded-md border border-gray-300 px-3">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" onClick={save} className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-emerald-800 px-4 font-semibold text-white">
            <FiSave aria-hidden="true" />
            Save Draft
          </button>
          {contentId && (
            <>
              <button type="button" onClick={() => dispatch(publishPastoralContent(contentId))} className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-emerald-800 px-4 font-semibold text-emerald-900">
                <FiSend aria-hidden="true" />
                Publish
              </button>
              <button type="button" onClick={() => dispatch(unpublishPastoralContent(contentId))} className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-gray-500 px-4 font-semibold text-gray-800">
                <FiEyeOff aria-hidden="true" />
                Unpublish
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default PastoralContentEditor;
